import { formatZonedTime } from "../../../lib/time";

/**
 * Build the browser-tooltip (title-attribute) string for a calendar event
 * on the Create/Edit Poll page.
 *
 * Friend overlay events  →  "10:00 AM – 11:00 AM: Busy (alice@example.com)"
 * Self overlay events    →  "10:00 AM – 11:00 AM: Quarterly planning"
 *                           (no identifier — it's obviously yours)
 * Non-overlay slot events →  undefined (let RBC use its default)
 *
 * The identifier comes from `getUserIdentity(profile).publicIdentifier`,
 * respecting the friend's own settings-page choice (qsUsername /
 * discordUsername / email). If profiles aren't loaded yet, falls back
 * gracefully to no identifier so tooltips don't read "(undefined)".
 *
 * Inputs:
 *   event                : RBC event with `start`, `end`, `title`,
 *                          `resource: { kind, ownerId, isSelf? }`
 *   identifierByOwnerId  : map<ownerId, string> built from the profile
 *                          fetch — falsy values mean "no identifier known"
 *   timeZone             : optional tz string (defaults to local)
 */
export function buildOverlayTooltip({
  event,
  identifierByOwnerId = {},
  timeZone,
} = {}) {
  if (!event || typeof event !== "object") return undefined;
  const r = event.resource || {};
  const kind = r.kind;
  const isOverlay = kind === "mark" || kind === "overlay-busy";
  if (!isOverlay) return undefined;

  const startLabel =
    event.start instanceof Date
      ? formatZonedTime(event.start, timeZone, "h:mm a", { showTimeZone: false })
      : "";
  const endLabel =
    event.end instanceof Date
      ? formatZonedTime(event.end, timeZone, "h:mm a", { showTimeZone: false })
      : "";
  const timeLabel =
    event.allDay === true
      ? "All day"
      : startLabel && endLabel
        ? `${startLabel} – ${endLabel}`
        : startLabel || endLabel || "";

  const title =
    typeof event.title === "string" && event.title ? event.title : "";

  // Self events: tooltip without identifier.
  if (r.isSelf === true) {
    return timeLabel && title ? `${timeLabel}: ${title}` : title || timeLabel;
  }

  // Friend overlay events: append the public identifier in parens.
  const identifier = identifierByOwnerId?.[r.ownerId] || null;
  const base = timeLabel && title ? `${timeLabel}: ${title}` : title || timeLabel;
  return identifier ? `${base} (${identifier})` : base;
}
